import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { Therapist } from "../types/therapist";
import type { ScheduleTherapist } from "../types/schedule";

const STATIC_JSON_URL = "/data/public-therapists.json";
const FIRESTORE_CACHE_DOC = doc(db, "_cache", "publicTherapists");

export type ScheduleTherapistListItem = ScheduleTherapist;

type StaticPayload = {
  therapists?: Therapist[];
  updatedAt?: string;
  count?: number;
};

let inflight: Promise<Therapist[]> | null = null;

function normalizeTherapists(value: unknown): Therapist[] {
  if (!Array.isArray(value)) return [];
  return value.filter((t): t is Therapist => Boolean(t && typeof t === "object" && (t as Therapist).id));
}

async function loadFromStaticJson(): Promise<Therapist[] | null> {
  try {
    // cache-bust leve: o JSON na Hostinger atrasa o deploy e o browser costuma
    // cachear /data/* — sem isso a vitrine “não atualiza” após desmarcar.
    const res = await fetch(`${STATIC_JSON_URL}?t=${Date.now()}`, {
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as StaticPayload;
    const list = normalizeTherapists(data.therapists);
    return list.length > 0 ? list : null;
  } catch {
    return null;
  }
}

async function loadFromFirestore(): Promise<Therapist[] | null> {
  try {
    const snap = await getDoc(FIRESTORE_CACHE_DOC);
    if (!snap.exists()) return null;
    // Lista vazia é válida (ninguém na vitrine) — não tratar como “sem dados”.
    return normalizeTherapists(snap.data()?.therapists);
  } catch {
    return null;
  }
}

async function loadFromCallable(): Promise<Therapist[] | null> {
  try {
    const fn = httpsCallable<void, { success: boolean; therapists: Therapist[] }>(
      functions,
      "getPublicTherapists"
    );
    const result = await fn();
    if (!result.data?.success) return null;
    return normalizeTherapists(result.data.therapists);
  } catch {
    return null;
  }
}

/**
 * Carrega terapeutas da vitrine pública.
 * 1) JSON estático (Hostinger) — só preview rápido via onFast
 * 2) Firestore _cache/publicTherapists — fonte de verdade (atualiza ao marcar/desmarcar)
 * 3) Cloud Function — fallback
 *
 * Importante: o onFast NÃO pode rodar depois do resultado final (race que
 * recolocava nomes já desmarcados na tela).
 */
export async function fetchPublicTherapists(options?: {
  onFast?: (therapists: Therapist[]) => void;
}): Promise<Therapist[]> {
  if (inflight) return inflight;

  inflight = (async () => {
    let settled = false;
    const staticPromise = loadFromStaticJson();
    const firestorePromise = loadFromFirestore();

    void staticPromise.then((staticList) => {
      if (settled) return;
      if (staticList?.length && options?.onFast) {
        options.onFast(staticList);
      }
    });

    const [firestoreList, staticList] = await Promise.all([firestorePromise, staticPromise]);
    settled = true;

    // Firestore é a fonte de verdade (inclui lista vazia).
    if (firestoreList !== null) return firestoreList;
    if (staticList !== null) return staticList;

    const callableList = await loadFromCallable();
    return callableList ?? [];
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function toScheduleTherapists(therapists: Therapist[]): ScheduleTherapist[] {
  return therapists.map((t) => ({
    id: t.id,
    nomePublico: t.nomePublico,
    title: t.title || "",
    sessionDuration: t.sessionDuration || 60,
    horariosDisponiveis: t.horariosDisponiveis || {},
  }));
}

/** Pré-carrega JSON estático + Firestore (hover no menu). */
export function prefetchPublicTherapists(): void {
  void loadFromStaticJson();
  void loadFromFirestore();
}
